import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useModalStore } from "store/modal/hooks/useModalStore";

import { toast } from "@krea/shared/design-system/utils/toast";

import { formatLenderResponseFormData } from "@/components/lender-application-details/bids/bidHelpers";
import { useOptimisticallyUpdateLenderApplication } from "@/features/lender-applications/hooks/use-optimistically-update-lender-application";
import type { ILenderApplicationExtended } from "@/features/lender-applications/types/lender-application";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import { mutationKeyMap } from "@/features/query-client";

import {
  placeBid,
  updateBid,
  updateLenderResponseCreditTermsFile,
} from "../../api";
import type { ILenderResponseInput } from "../../types/lender-response";

interface ISaveBidParams {
  lenderCompanyId: number;
  lenderApplicationId: number;
  lenderResponseId?: number;
  input: ILenderResponseInput;
}

export const useSaveBid = () => {
  const { setModalLoading, closeModal } = useModalStore((state) => ({
    setModalLoading: state.setModalLoading,
    closeModal: state.closeModal,
  }));

  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const optimisticallyUpdateLenderApplication =
    useOptimisticallyUpdateLenderApplication();

  return useMutation<ILenderApplicationExtended, AxiosError, ISaveBidParams>({
    mutationFn: async ({
      lenderCompanyId,
      lenderApplicationId,
      lenderResponseId,
      input,
    }) => {
      let responseData: ILenderApplicationExtended;

      const isFeatureInterestRatePerYearActivated =
        lenderCompany?.isFeatureInterestRatePerYearActivated;

      // Remove credit terms file from data to avoid it being sent to the server
      // As we are sending it separately with form data in a separate request

      const { creditTermsFile, ...restInput } = input;

      // ===== Handle credit terms file (If we have an existing file with an id that can be "reused") =====

      // When editing an existing lender response.
      // If the credit terms file is already uploaded, we need to send the id to the server. to re-store the file.
      // Otherwise, we send the file as form data
      const dataToSend = formatLenderResponseFormData(
        {
          ...restInput,
          creditTermsFile:
            creditTermsFile && "id" in creditTermsFile && creditTermsFile.id
              ? creditTermsFile
              : undefined,
        },
        "TO_MONTHLY",
        isFeatureInterestRatePerYearActivated,
      );

      // ===== Handle credit terms file =====

      if (lenderResponseId) {
        const response = await updateBid(
          lenderCompanyId,
          lenderApplicationId,
          lenderResponseId,
          dataToSend,
        );

        responseData = response;
      } else {
        const response = await placeBid(
          lenderCompanyId,
          lenderApplicationId,
          dataToSend,
        );

        responseData = response;
      }

      // ===== Handle credit terms file (if its multipart form data) =====
      if (creditTermsFile && creditTermsFile instanceof File) {
        // If we have a file, we need to update the latest lender response with the file
        // using a multipart form data request
        const latestLenderResponse = responseData.lenderResponses?.sort(
          (a, b) => {
            return (
              new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
            );
          },
        )[0];

        if (latestLenderResponse) {
          await updateLenderResponseCreditTermsFile(
            lenderCompanyId,
            latestLenderResponse.id,
            {
              file: creditTermsFile,
            },
          );
        }
      }
      // ===== Handle credit terms file (if its multipart form data) =====

      return responseData;
    },
    mutationKey: mutationKeyMap.saveBid,
    onMutate: () => {
      setModalLoading(true);
    },

    onSuccess: async (response, { lenderApplicationId }) => {
      optimisticallyUpdateLenderApplication(lenderApplicationId, response);
    },
    onError: (_error, { lenderApplicationId }) => {
      toast.error(`Placing bid for application ${lenderApplicationId} failed`);
    },
    onSettled: () => {
      setModalLoading(false);
      closeModal();
    },
  });
};
